import React, { useState, useRef } from "react";
import { X, Camera, Loader2, User, Check, Zap, Crown, Star } from "lucide-react";
import { uploadUserAvatar, updateUser } from "../api";

const SUBSCRIPTION_TIERS = [
  {
    id: 'free',
    name: 'Free',
    icon: Star,
    price: '$0',
    period: '/month',
    credits: '10 cases',
    features: [
      '3,000 A.I credits per month',
      '9-hour processing time',
      'Case Management Dashboard',
      'P&L Tracker',
      'Executive Dashboard',
      'Case Assistant',
      'Notes',
      'Meeting Assistant'
    ],
    color: 'slate',
    highlight: false
  },
  {
    id: 'starter',
    name: 'Starter',
    icon: Zap,
    price: '$39.99',
    period: '/month',
    features: [
      '10,000 A.I credits per month',
      '6-hour processing time',
      'Priority support',
      'Case Management Dashboard',
      'P&L Tracker',
      'Executive Dashboard',
      'Team: 3 users',
      'Case Assistant',
      'Notes',
      'Meeting Assistant'
    ],
    color: 'sky',
    highlight: false
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: Crown,
    price: '$79.99',
    period: '/month',
    features: [
      '20,000 A.I credits per month',
      '3-hour processing time',
      'Priority support',
      'Case Management Dashboard',
      'P&L Tracker',
      'Executive Dashboard',
      'Team: 10 users',
      'Case Assistant',
      'Notes',
      'Meeting Assistant'
    ],
    color: 'violet',
    highlight: true
  }
];

const AccountSettingsModal = ({
  isAccountSettingsOpen,
  setIsAccountSettingsOpen,
  userEmail,
  userMeta,
  handleLogout,
  onUpdateMeta,
  onUpgradePlan,
  onOpenBillingPortal,
  isPaymentProcessing
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('profile'); // 'profile' or 'subscription'
  const fileInputRef = useRef(null);

  const currentTier = (userMeta.membership || 'Free').toLowerCase();

  if (!isAccountSettingsOpen) return null;

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith('image/')) {
      alert("Please upload an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB.");
      return;
    }

    setIsUploading(true);
    try {
      // 1. Upload the image
      const uploadRes = await uploadUserAvatar(file);
      const newAvatarUrl = uploadRes.data.avatar_url;

      // 2. Update the user profile with the new URL
      await updateUser({ avatar_url: newAvatarUrl });

      // 3. Optional: Trigger a refresh of user data in parent
      if (onUpdateMeta) {
        onUpdateMeta({ ...userMeta, avatar_url: newAvatarUrl });
      }

    } catch (error) {
      console.error("Avatar upload failed:", error);
      alert("Failed to update avatar. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpgradeTier = (tierId) => {
    if (tierId === 'free') return; // Can't "upgrade" to free
    if (tierId === currentTier) return; // Already on this tier

    // Call the upgrade function with tier info
    onUpgradePlan(tierId);
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b dark:border-slate-800 border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Account
              </p>
              <p className="text-lg font-bold dark:text-white text-slate-900">Account Settings</p>
              <p className="text-sm dark:text-slate-400 text-slate-500">
                Manage your profile, subscription, and billing
              </p>
            </div>
            <button
              onClick={() => setIsAccountSettingsOpen(false)}
              className="p-2 rounded-full text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b dark:border-slate-800 border-slate-200 -mb-px">
            <button
              onClick={() => setSelectedTab('profile')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${selectedTab === 'profile'
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
              Profile
              {selectedTab === 'profile' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
              )}
            </button>
            <button
              onClick={() => setSelectedTab('subscription')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${selectedTab === 'subscription'
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
              Subscription
              {selectedTab === 'subscription' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 dark:bg-yellow-400"></div>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {selectedTab === 'profile' ? (
            /* Profile Tab */
            <div className="max-w-2xl mx-auto">
              <div className="space-y-4 dark:bg-slate-950/60 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="relative group">
                    <div className="h-16 w-16 rounded-2xl dark:bg-slate-800 bg-slate-200 border-2 dark:border-slate-700 border-slate-300 overflow-hidden flex items-center justify-center">
                      {isUploading ? (
                        <Loader2 className="w-6 h-6 text-yellow-500 animate-spin" />
                      ) : userMeta.avatar_url ? (
                        <img src={userMeta.avatar_url} alt="Profile" className="h-full w-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 dark:text-slate-500 text-slate-400" />
                      )}
                    </div>
                    <button
                      onClick={handleAvatarClick}
                      disabled={isUploading}
                      className="absolute -bottom-1 -right-1 p-1.5 rounded-lg bg-yellow-600 text-white shadow-lg hover:bg-yellow-500 transition-colors disabled:opacity-50"
                    >
                      <Camera size={14} />
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold dark:text-white text-slate-900">Profile Picture</p>
                    <p className="text-xs text-slate-500">JPG, PNG or GIF. Max 5MB.</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm dark:text-slate-200 text-slate-700 border-t dark:border-slate-800 border-slate-200 pt-4">
                  <div className="flex justify-between">
                    <span>Email</span>
                    <span className="text-slate-500">
                      {userEmail || userMeta.email || "Unknown"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Membership</span>
                    <span className="text-slate-500">
                      {userMeta.membership || "Free"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Credits</span>
                    <span className="text-slate-500">{userMeta.credit ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Subscription Tab */
            <div className="space-y-6">
              <div className="text-center max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold dark:text-white text-slate-900 mb-2">
                  Choose Your Plan
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Upgrade anytime. Downgrade or cancel at any time. All plans include secure Stripe checkout.
                </p>
              </div>

              {/* Tier Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                {SUBSCRIPTION_TIERS.map((tier) => {
                  const Icon = tier.icon;
                  const isCurrentTier = tier.id === currentTier;
                  const canUpgrade = tier.id !== 'free' && tier.id !== currentTier;

                  return (
                    <div
                      key={tier.id}
                      className={`relative rounded-xl border-2 overflow-hidden transition-all ${tier.highlight
                        ? 'border-violet-500 dark:border-violet-400 shadow-xl shadow-violet-500/20'
                        : isCurrentTier
                          ? 'border-yellow-500 dark:border-yellow-400'
                          : 'border-slate-200 dark:border-slate-700'
                        } ${tier.highlight ? 'transform scale-105' : ''}`}
                    >
                      {/* Highlight Badge */}
                      {tier.highlight && (
                        <div className="absolute top-0 right-0 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                          RECOMMENDED
                        </div>
                      )}

                      {/* Current Plan Badge */}
                      {isCurrentTier && (
                        <div className="absolute top-0 left-0 bg-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-br-lg">
                          CURRENT PLAN
                        </div>
                      )}

                      <div className="p-6 dark:bg-slate-900 bg-white">
                        {/* Icon & Name */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`p-3 rounded-xl ${tier.color === 'violet'
                            ? 'bg-violet-500/10 text-violet-500'
                            : tier.color === 'sky'
                              ? 'bg-yellow-500/10 text-yellow-500'
                              : 'bg-slate-500/10 text-slate-500'
                            }`}>
                            <Icon size={24} />
                          </div>
                          <div>
                            <h4 className="font-bold text-lg dark:text-white text-slate-900">
                              {tier.name}
                            </h4>
                            <p className="text-xs text-slate-500">{tier.credits}</p>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="mb-6">
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold dark:text-white text-slate-900">
                              {tier.price}
                            </span>
                            <span className="text-slate-500 text-sm">{tier.period}</span>
                          </div>
                        </div>

                        {/* Features */}
                        <ul className="space-y-3 mb-6">
                          {tier.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <Check size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="dark:text-slate-300 text-slate-700">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* CTA Button */}
                        {isCurrentTier ? (
                          <button
                            type="button"
                            onClick={onOpenBillingPortal}
                            disabled={isPaymentProcessing || tier.id === 'free'}
                            className={`w-full py-3 rounded-lg font-semibold transition-all ${tier.id === 'free'
                              ? 'bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-not-allowed'
                              : 'border-2 border-yellow-500 dark:border-yellow-400 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-500/10'
                              }`}
                          >
                            {tier.id === 'free' ? 'Current Plan' : 'Manage Billing'}
                          </button>
                        ) : canUpgrade ? (
                          <button
                            type="button"
                            onClick={() => handleUpgradeTier(tier.id)}
                            disabled={isPaymentProcessing}
                            className={`w-full py-3 rounded-lg font-semibold transition-all hover:scale-105 active:scale-95 ${tier.highlight
                              ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg hover:shadow-xl'
                              : 'bg-yellow-600 text-white hover:bg-yellow-500'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {isPaymentProcessing ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 size={16} className="animate-spin" />
                                Processing...
                              </span>
                            ) : (
                              `Select ${tier.name}`
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="w-full py-3 rounded-lg font-semibold bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-not-allowed"
                          >
                            {tier.id === 'free' ? 'Contact Support to Downgrade' : 'Lower Tier'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsModal;
